import axios from 'axios'
import { Message } from 'element-ui'
import router from '../router/index.js'
import qs from 'qs'
import md5 from 'js-md5'
import common from '@/utils/utils'
import { getUUID, setUUID } from '@/utils/local'

// ─── baseURL 映射表 ───────────────────────────────────────────────────────────
// 通过在请求函数的第二个参数传入 baseType 字符串来切换目标服务
// 例：request({ url: '/xxx', method: 'POST', data }, 'ai-classroom')
// 未传或传 undefined 时默认使用 VUE_APP_HOST
const BASE_URL_MAP = {
  // 默认主服务（教师端 API）
  default: process.env.VUE_APP_HOST,
  // AI 课堂服务
  'ai-classroom': process.env.VUE_APP_AI_CLASSROOM_API_URL
}

// ─── 创建 axios 实例 ──────────────────────────────────────────────────────────
// baseURL 在请求拦截器中根据 baseType 动态设置，这里先用默认值占位
const request = axios.create({
  baseURL: BASE_URL_MAP.default,
  timeout: 15000,
  headers: {
    'Content-Type': 'application/x-www-form-urlencoded'
  }
})

// ─── 请求拦截器 ───────────────────────────────────────────────────────────────
// 统一注入鉴权参数、签名、设备信息等公共字段
request.interceptors.request.use(
  config => {
    // 根据 config 上挂载的 baseType 动态切换 baseURL
    // baseType 由调用方通过 request 的第二个参数传入（见下方导出函数）
    if (config.baseType && BASE_URL_MAP[config.baseType]) {
      config.baseURL = BASE_URL_MAP[config.baseType]
    }

    const sessionId = sessionStorage.getItem('sessionId')
    const teacherId = sessionStorage.getItem('teacherId')
    const version = '3.0.0'
    const device = 'web'

    // 公共参数，每个请求都会携带
    const dataParam = {
      appVersion: version,
      belong: '1',
      device
    }

    // sessionId / teacherId 存在时才注入，避免覆盖调用方显式传入的值
    if (sessionId && sessionId !== 'null' && !config.data?.sessionId) {
      dataParam.sessionId = sessionId
    }
    if (teacherId && teacherId !== 'null' && !config.data?.teacherId) {
      dataParam.teacherId = teacherId
    }

    // 根据 Content-Type 将公共参数合并到请求体，并收集用于签名的字符串
    let signStr = ''
    if (config.method === 'post') {
      if (config.headers['Content-Type'] === 'application/json') {
        // JSON 请求：直接合并对象
        config.data = {
          ...config.data,
          ...dataParam
        }
      } else if (config.headers['Content-Type'] !== 'multipart/form-data') {
        // 表单请求：通过 qs 序列化后合并
        config.data = qs.stringify({
          ...qs.parse(config.data),
          ...dataParam
        })
      } else {
        // 文件上传（FormData）：逐个 append 公共参数
        Object.keys(dataParam).forEach(k => {
          config.data.append(k, dataParam[k])
        })
      }
      signStr = JSON.stringify(config.data)
    } else if (config.method === 'get') {
      config.params = {
        ...dataParam,
        ...config.params
      }
      signStr = JSON.stringify(config.params)
    }

    // 签名算法：apiv2 接口用 md5(signStr + '_Sts100#@')，其余用 md5(md5(signStr))
    let sign
    if (config.url.includes('apiv2')) {
      sign = md5(signStr + '_Sts100#@')
    } else {
      sign = md5(md5(signStr))
    }

    // 设备唯一标识，首次生成后持久化到本地存储
    let udid = getUUID()
    if (!udid) {
      udid = common.getUUID(32)
      setUUID(udid)
    }

    config.headers = {
      apps: 2,
      ...config.headers,
      udid,
      sign,
      release: version,
      device,
      thirdTypeId: 1,
      authToken: sessionId
    }

    return config
  },
  error => {
    console.log(error)
    return Promise.reject(error)
  }
)

// ─── 响应拦截器 ───────────────────────────────────────────────────────────────
// 统一处理业务状态码：11=成功，22=登录过期，其余=业务错误
request.interceptors.response.use(
  response => {
    const currentRoute = router.currentRoute
    const res = response.data

    if (res.state || res.state === 0) {
      res.state += ''

      if (res.state === '11') {
        // 请求成功，直接返回业务数据
        return res
      } else if (res.state === '22') {
        // 登录过期处理
        console.log(currentRoute.path)
        // 内嵌 iframe 页面通过 postMessage 通知父页面处理 token 过期
        const iframePathList = ['/transfer', '/exam-report-v2']
        if (iframePathList.includes(currentRoute.path)) {
          window.parent.postMessage({ type: 'token-expire' }, '*')
        } else {
          sessionStorage.clear()
          Message.error('登陆过期，请重新登录！')
          router.replace({ name: 'login' })
        }
      } else {
        // 其他业务错误：有 note 则弹出错误提示
        if (res.note) {
          Message.error(res.note)
        } else if (res) {
          window.location.href = res
        }
        return res
      }
    } else {
      // 非标准业务响应（如文件流等），直接返回原始 response
      return response
    }
  },
  error => {
    Message.error('网络异常，请退出重试！')
    console.log(error)
    return Promise.reject(error)
  }
)

// ─── 导出请求函数 ─────────────────────────────────────────────────────────────
// 封装一层，支持通过第二个参数 baseType 切换目标服务的 baseURL
// @param {Object} config   - axios 请求配置，与直接使用 axios 一致
// @param {string} baseType - 可选，目标服务标识，取值见 BASE_URL_MAP
//                            'ai-classroom' → VUE_APP_AI_CLASSROOM_API_URL
//                            不传或传其他值  → VUE_APP_HOST（默认）
export default function requestWithBase(config, baseType) {
  // 将 baseType 挂到 config 上，由请求拦截器读取并切换 baseURL
  return request({ ...config, baseType })
}
